// @ds-nuevo DepartmentPicker: selector de un departamento por código de activación, con búsqueda cuando la lista es larga
import React, { FC } from 'react';
import {
  Autocomplete,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import type { Department } from '../../config/businesses';

const SEARCH_THRESHOLD = 10;

const buildSearchRegex = (input: string): RegExp => {
  try {
    return new RegExp(input, 'i');
  } catch {
    const escaped = input.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return new RegExp(escaped, 'i');
  }
};

interface DepartmentPickerProps {
  id: string;
  label: string;
  placeholder: string;
  options: Department[];
  value: string;
  onChange: (activationCode: string) => void;
}

export const DepartmentPicker: FC<DepartmentPickerProps> = ({
  id,
  label,
  placeholder,
  options,
  value,
  onChange,
}) => {
  if (options.length > SEARCH_THRESHOLD) {
    const selected = options.find(d => d.activationCode === value) ?? null;
    return (
      <Autocomplete<Department>
        id={id}
        options={options}
        value={selected}
        onChange={(_, option) => onChange(option?.activationCode ?? '')}
        getOptionLabel={option => option.name}
        isOptionEqualToValue={(a, b) => a.activationCode === b.activationCode}
        filterOptions={(list, { inputValue }) => {
          const term = inputValue.trim();
          if (!term) return list;
          const regex = buildSearchRegex(term);
          return list.filter(option => regex.test(option.name));
        }}
        noOptionsText="Sin resultados"
        fullWidth
        ListboxProps={{ style: { maxHeight: 320 } }} // ds-ok: alto heredado del selector anterior
        renderInput={params => (
          <TextField
            {...params}
            label={label}
            placeholder={placeholder}
            variant="outlined"
          />
        )}
      />
    );
  }

  return (
    <FormControl fullWidth>
      <InputLabel id={`${id}-label`}>{label}</InputLabel>
      <Select
        value={value}
        onChange={event => onChange(event.target.value)}
        labelId={`${id}-label`}
        id={id}
        label={label}
        variant="outlined"
      >
        {options.map(dept => (
          <MenuItem key={dept.activationCode} value={dept.activationCode}>
            {dept.name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};
